<template>
    <!--
        A multiple select that can be CLOSED.

        Nuxt UI's own multi-select stays open while items are ticked, which is right —
        picking three room types is one act — and then offers no way out but a click on
        the page behind it. Nobody guesses that. So the dropdown carries a footer with
        the count and a "Potrdi" that shuts it, which is the button people look for.

        Everything else is USelectMenu untouched: props, slots and events pass straight
        through, so a call site swaps the tag and changes nothing else.
    -->
    <USelectMenu
        v-bind="$attrs"
        v-model="model"
        v-model:open="open"
        :multiple="props.multiple"
    >
        <template v-for="(_, name) in $slots" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps ?? {}" />
        </template>

        <template v-if="props.multiple" #content-bottom>
            <div class="flex items-center justify-between gap-3 border-t border-default px-2 py-1.5">
                <span class="text-xs text-muted">{{ $t('general.selected_count', { count: selectedCount }) }}</span>

                <UButton
                    type="button"
                    size="xs"
                    icon="i-lucide-check"
                    :label="$t('general.select_confirm')"
                    @click.stop.prevent="open = false"
                />
            </div>
        </template>
    </USelectMenu>
</template>

<script lang="ts" setup generic="T extends SelectMenuItem | SelectMenuItem[]">
    import type { SelectMenuItem } from '@nuxt/ui'
    import { computed, ref } from 'vue'

    /**
     * `multiple` is a prop rather than an attribute because the footer depends on it:
     * a single select closes itself the moment something is picked and must not grow a
     * confirm button. It defaults to true — the whole reason this wrapper exists.
     */
    const props = withDefaults(defineProps<{
        modelValue?: T
        multiple?:   boolean
    }>(), {
        modelValue: undefined,
        multiple:   true,
    })

    const emit = defineEmits<(e: 'update:modelValue', value: T) => void>()

    defineOptions({ inheritAttrs: false })

    const open = ref(false)

    const model = computed({
        get: () => props.modelValue,
        set: (value?: T) => emit('update:modelValue', value as T),
    })

    const selectedCount = computed(() => Array.isArray(props.modelValue) ? props.modelValue.length : 0)
</script>
